import _extends from '@babel/runtime/helpers/esm/extends';
import * as React from 'react';
import * as THREE from 'three';
import { useThree, useFrame } from '@react-three/fiber';
import { Line } from '@react-three/drei';

const FrontEdges = /* @__PURE__ */ React.forwardRef(
  (
    {
      threshold = 15,
      geometry: explicitGeometry,
      frontOnly = true, // 👈 added flag
      ...props
    },
    fref
  ) => {
    const ref = React.useRef(null);
    const { camera } = useThree(); // 👈 automatically get the scene camera
    React.useImperativeHandle(fref, () => ref.current, []);

    const tmpPoints = React.useMemo(() => [0, 0, 0, 1, 0, 0], []);
    const memoizedGeometry = React.useRef(null);
    const memoizedThreshold = React.useRef(null);

    // 👇 same as original drei behavior
    React.useLayoutEffect(() => {
      const parent = ref.current.parent;
      const geometry =
        explicitGeometry !== null && explicitGeometry !== void 0
          ? explicitGeometry
          : parent == null
          ? void 0
          : parent.geometry;
      if (!geometry) return;
      const cached =
        memoizedGeometry.current === geometry &&
        memoizedThreshold.current === threshold;
      if (cached) return;
      memoizedGeometry.current = geometry;
      memoizedThreshold.current = threshold;
      const points = new THREE.EdgesGeometry(geometry, threshold).attributes
        .position.array;
      ref.current.geometry.setPositions(points);
      ref.current.geometry.attributes.instanceStart.needsUpdate = true;
      ref.current.geometry.attributes.instanceEnd.needsUpdate = true;
      ref.current.computeLineDistances();
    });

    // 👇 added front-facing visibility check (lightweight)
    useFrame(() => {
      if (!frontOnly || !ref.current?.parent) return;

      const parent = ref.current.parent;
      const objWorld = new THREE.Vector3();
      const normal = new THREE.Vector3(0, 0, 1); // assumes plane faces +Z

      parent.getWorldPosition(objWorld);
      normal.applyQuaternion(parent.getWorldQuaternion(new THREE.Quaternion()));

      const camDir = camera.position.clone().sub(objWorld).normalize();
      const facing = normal.dot(camDir) > 0;

      ref.current.visible = facing;
    });

    return /*#__PURE__*/ React.createElement(
      Line,
      _extends(
        {
          segments: true,
          points: tmpPoints,
          ref: ref,
          raycast: () => null,
        },
        props
      )
    );
  }
);

export { FrontEdges };
